import { useAuth } from '@/app/_auth/auth-hooks'
import { FrequencyValue, ScheduleResponse } from '@/app/schedule/_utils/types'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getFrequencyInSeconds } from './use-create-schedule-mutation'

const GET_SCHEDULE_ERROR = 'FAILED_TO_GET_SCHEDULE'
const UPDATE_SCHEDULE_ERROR = 'FAILED_TO_UPDATE_SCHEDULE'
const DELETE_SCHEDULE_ERROR = 'FAILED_TO_DELETE_SCHEDULE'
const PAUSE_SCHEDULE_ERROR = 'FAILED_TO_PAUSE_SCHEDULE'
const UNPAUSE_SCHEDULE_ERROR = 'FAILED_TO_UNPAUSE_SCHEDULE'

type UpdateScheduleRequest = {
  agentId: string
  toolId: string
  startDate: Date
  endDate?: Date
  startTime: string
  frequency: FrequencyValue
  parameters?: Record<string, string>
}

type UseScheduleQueryOptions = {
  scheduleId: string
  queryOptions?: Omit<UseQueryOptions<ScheduleResponse>, 'queryFn' | 'queryKey'>
}

function useScheduleQuery({ scheduleId, queryOptions = {} }: UseScheduleQueryOptions) {
  const { apiRequest, domainId } = useApiRequest()

  return useQuery({
    queryKey: scheduleKeyFactory({ scheduleId, domainId }),
    queryFn: async (): Promise<ScheduleResponse> => {
      const response = await apiRequest({
        path: taskManagerApiPaths.schedule({ domainId, scheduleId }),
      })

      if (!response.ok) {
        throw new Error(GET_SCHEDULE_ERROR)
      }

      const data = (await response.json()) as ScheduleResponse
      return data
    },
    enabled: !!scheduleId,
    ...queryOptions,
  })
}

const transformUpdateToAPIRequest = (params: UpdateScheduleRequest): Partial<ScheduleResponse> => {
  const { agentId, toolId, startDate, endDate, startTime, frequency, parameters } = params

  const [hours, minutes] = startTime.split(':').map(Number)
  const startDateTime = dayjs(startDate)
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .toISOString()

  const endDateTime = endDate ? endDate.toISOString() : undefined

  const frequencyInSeconds = getFrequencyInSeconds(frequency)

  return {
    start_time: startDateTime,
    end_time: endDateTime,
    frequency: frequencyInSeconds,
    agent_id: agentId,
    tool_id: toolId,
    tool_args: parameters,
  }
}

function useUpdateScheduleMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['update-schedule'],
    mutationFn: async ({
      scheduleId,
      updateData,
    }: {
      scheduleId: string
      updateData: UpdateScheduleRequest
    }): Promise<ScheduleResponse> => {
      const requestBody = transformUpdateToAPIRequest(updateData)

      const response = await apiRequest({
        path: taskManagerApiPaths.schedule({ domainId, scheduleId }),
        options: {
          method: 'PUT',
          body: JSON.stringify(requestBody),
        },
      })

      if (!response.ok) {
        throw new Error(UPDATE_SCHEDULE_ERROR)
      }

      const data = (await response.json()) as ScheduleResponse
      return data
    },
    onSuccess: (updatedSchedule) => {
      queryClient.setQueryData(
        scheduleKeyFactory({ scheduleId: updatedSchedule.id, domainId }),
        updatedSchedule,
      )

      queryClient.invalidateQueries({ queryKey: ['schedule-tasks', domainId] })
    },
  })
}

function useDeleteScheduleMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['delete-schedule'],
    mutationFn: async (scheduleId: string): Promise<void> => {
      const response = await apiRequest({
        path: taskManagerApiPaths.schedule({ domainId, scheduleId }),
        options: {
          method: 'DELETE',
        },
      })

      if (!response.ok) {
        throw new Error(DELETE_SCHEDULE_ERROR)
      }
    },
    onSuccess: (_, scheduleId) => {
      queryClient.removeQueries({
        queryKey: scheduleKeyFactory({ scheduleId, domainId }),
      })

      queryClient.invalidateQueries({ queryKey: ['schedule-tasks', domainId] })
    },
  })
}

function usePauseScheduleMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['pause-schedule'],
    mutationFn: async (scheduleId: string): Promise<ScheduleResponse> => {
      const response = await apiRequest({
        path: taskManagerApiPaths.pauseSchedule({ domainId, scheduleId }),
        options: {
          method: 'POST',
        },
      })

      if (!response.ok) {
        throw new Error(PAUSE_SCHEDULE_ERROR)
      }

      const data = (await response.json()) as ScheduleResponse
      return data
    },
    onSuccess: (updatedSchedule) => {
      queryClient.setQueryData(
        scheduleKeyFactory({ scheduleId: updatedSchedule.id, domainId }),
        updatedSchedule,
      )

      queryClient.invalidateQueries({ queryKey: ['schedule-tasks', domainId] })
    },
  })
}

function useUnpauseScheduleMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['unpause-schedule'],
    mutationFn: async (scheduleId: string): Promise<ScheduleResponse> => {
      const response = await apiRequest({
        path: taskManagerApiPaths.unpauseSchedule({ domainId, scheduleId }),
        options: {
          method: 'POST',
        },
      })

      if (!response.ok) {
        throw new Error(UNPAUSE_SCHEDULE_ERROR)
      }

      const data = (await response.json()) as ScheduleResponse
      return data
    },
    onSuccess: (updatedSchedule) => {
      queryClient.setQueryData(
        scheduleKeyFactory({ scheduleId: updatedSchedule.id, domainId }),
        updatedSchedule,
      )

      queryClient.invalidateQueries({ queryKey: ['schedule-tasks', domainId] })
    },
  })
}

const scheduleKeyFactory = ({ scheduleId, domainId }: { scheduleId: string; domainId: string }) => [
  'schedule',
  domainId,
  scheduleId,
]

export {
  DELETE_SCHEDULE_ERROR,
  GET_SCHEDULE_ERROR,
  PAUSE_SCHEDULE_ERROR,
  scheduleKeyFactory,
  UNPAUSE_SCHEDULE_ERROR,
  UPDATE_SCHEDULE_ERROR,
  useDeleteScheduleMutation,
  usePauseScheduleMutation,
  useScheduleQuery,
  useUnpauseScheduleMutation,
  useUpdateScheduleMutation,
  type UpdateScheduleRequest,
}
